import React, { useState, useEffect } from 'react';
import { authService } from '../auth/authService';
import { getAuthorizationService } from '../services/authorizationService';
import { PLATFORM_CONFIG } from '../config/appConfig';
import Layout from './layout/Layout';
import Card from './ui/Card';
import Button from './ui/Button';
import Spinner from './ui/Spinner';
import SelkirkIcon from './icons/SelkirkIcon';
import IconText from './icons/IconText';
import { ICON } from '../icons/iconIds';
import './AppLauncher.css';

const AppLauncher = ({ user }) => {
  const [accessibleApps, setAccessibleApps] = useState([]);
  const [userGroups, setUserGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const authorizationService = getAuthorizationService(authService);

  useEffect(() => {
    initializeUserAccess();
  }, []);

  const initializeUserAccess = async () => {
    try {
      setLoading(true);
      
      // Initialize user permissions
      await authorizationService.initializeUserPermissions();
      
      // Get accessible applications
      const apps = authorizationService.getAccessibleApps();
      setAccessibleApps(apps);
      
      // Get user groups
      const groups = authorizationService.getUserGroupsWithNames();
      setUserGroups(groups);
      
    } catch (err) {
      console.error('Error initializing user access:', err);
      setError('Failed to load user permissions. Please try refreshing the page.');
    } finally {
      setLoading(false);
    }
  };

  const handleAppClick = (app) => {
    try {
      if (app.status === 'active') {
        authorizationService.navigateToApp(app.id);
      } else {
        alert(`${app.name} is currently in ${app.status} status and not available yet.`);
      }
    } catch (err) {
      alert(err.message);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: { text: 'Active', class: 'badge-active' },
      development: { text: 'Dev', class: 'badge-development' },
      planned: { text: 'Planned', class: 'badge-planned' }
    };
    return badges[status] || badges.planned;
  };

  if (loading) {
    return (
      <div className="app-launcher">
        <div className="loading-container">
          <Spinner size="large" />
          <p>Loading your applications...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="app-launcher">
        <div className="error-container">
          <IconText icon={ICON.launcherError} as="h2">
            Error Loading Applications
          </IconText>
          <p>{error}</p>
          <button onClick={initializeUserAccess} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <Layout user={user} showSidebar={false} currentRoute="/">
      <div className="app-launcher-content">
        {/* Hero Section */}
        <div className="hero-section">
          <div className="hero-background">
            <div className="hero-pattern"></div>
          </div>
          <div className="hero-content">
            <div className="brand-showcase">
              <h1 className="hero-title">
                Welcome to {PLATFORM_CONFIG.name}
              </h1>
              <p className="hero-subtitle">
                Unified data platform for mining operations
              </p>
            </div>
            
            <div className="user-welcome">
              <div className="user-greeting">
                <h2>Hello, {user?.name?.split(' ')[0] || 'User'}</h2>
                <p className="user-role">
                  You have access to {accessibleApps.length} application{accessibleApps.length !== 1 ? 's' : ''}
                </p>
              </div>
              <div className="user-groups">
                {userGroups.map((group) => (
                  <span key={group.id} className="group-badge premium">
                    <span className="badge-icon">
                      <SelkirkIcon name={ICON.launcherGroupBadge} size={16} decorative />
                    </span>
                    {group.displayName}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="stats-section">
          <div className="stats-grid">
            <Card variant="glass" className="stat-card">
              <div className="stat-content">
                <div className="stat-icon">
                  <SelkirkIcon name={ICON.launcherStatApps} decorative />
                </div>
                <div className="stat-info">
                  <h3 className="stat-number">{accessibleApps.length}</h3>
                  <p className="stat-label">Available Apps</p>
                </div>
              </div>
            </Card>
            
            <Card variant="glass" className="stat-card">
              <div className="stat-content">
                <div className="stat-icon">
                  <SelkirkIcon name={ICON.launcherStatSecurity} decorative />
                </div>
                <div className="stat-info">
                  <h3 className="stat-number">{userGroups.length}</h3>
                  <p className="stat-label">Security Groups</p>
                </div>
              </div>
            </Card>
            
            <Card variant="glass" className="stat-card">
              <div className="stat-content">
                <div className="stat-icon">
                  <SelkirkIcon name={ICON.launcherStatUptime} decorative />
                </div>
                <div className="stat-info">
                  <h3 className="stat-number">99.9%</h3>
                  <p className="stat-label">Uptime</p>
                </div>
              </div>
            </Card>
            
            <Card variant="glass" className="stat-card">
              <div className="stat-content">
                <div className="stat-icon">
                  <SelkirkIcon name={ICON.launcherStatVersion} decorative />
                </div>
                <div className="stat-info">
                  <h3 className="stat-number">v{PLATFORM_CONFIG.version}</h3>
                  <p className="stat-label">Platform Version</p>
                </div>
              </div>
            </Card>
          </div>
        </div>

        {/* Applications Section */}
        <div className="applications-section">
          <div className="section-header">
            <h2 className="section-title">Your Applications</h2>
            <p className="section-subtitle">
              Launch applications you have access to
            </p>
          </div>

          {accessibleApps.length === 0 ? (
            <Card className="no-apps-card" variant="elevated" padding="xl">
              <div className="no-apps-content">
                <div className="no-apps-illustration">
                  <div className="empty-state-icon">
                    <SelkirkIcon name={ICON.launcherNoApps} size={48} decorative />
                  </div>
                  <div className="empty-state-pattern"></div>
                </div>
                <h3>No Applications Available</h3>
                <p>You don't have access to any applications yet.</p>
                <p>Contact your administrator to request access to applications.</p>
                <div className="empty-state-actions">
                  <Button variant="primary" className="contact-admin-btn">
                    <span className="btn-icon">
                      <SelkirkIcon name={ICON.launcherContactIt} size={18} decorative />
                    </span>
                    Contact Administrator
                  </Button>
                  <Button variant="ghost" className="learn-more-btn">
                    Learn More
                  </Button>
                </div>
              </div>
            </Card>
          ) : (
            <div className="apps-grid enhanced">
              {accessibleApps.map((app) => {
                const badge = getStatusBadge(app.status);
                const isDisabled = app.status !== 'active';
                
                return (
                  <Card
                    key={app.id}
                    variant="glass"
                    hoverable={!isDisabled}
                    clickable={!isDisabled}
                    onClick={() => handleAppClick(app)}
                    className={`app-card enhanced ${isDisabled ? 'disabled' : ''}`}
                  >
                    <div className="app-card-header">
                      <div className="app-icon-container">
                        <div className="app-icon" style={{ color: app.color }}>
                          <SelkirkIcon name={app.iconId} size={32} decorative />
                        </div>
                        <div className="app-icon-glow" style={{ backgroundColor: app.color }}></div>
                      </div>
                      {badge && (
                        <span className={`status-badge modern ${badge.class}`}>
                          {badge.text}
                        </span>
                      )}
                    </div>
                    
                    <div className="app-card-body">
                      <h3 className="app-name">{app.name}</h3>
                      <p className="app-description">{app.description}</p>
                      
                      <div className="app-features">
                        <span className="feature-tag">Data Analytics</span>
                        <span className="feature-tag">Real-time</span>
                      </div>
                    </div>
                    
                    <div className="app-card-footer">
                      {!isDisabled ? (
                        <Button 
                          variant="primary" 
                          size="sm"
                          fullWidth
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAppClick(app);
                          }}
                        >
                          Launch Application
                        </Button>
                      ) : (
                        <Button variant="ghost" size="sm" fullWidth disabled>
                          Coming Soon
                        </Button>
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

      </div>
    </Layout>
  );
};

export default AppLauncher;
